import React, { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SellexxLogo from "./sellexx-logo";
import SearchBar from "./search_bar";
import CurrencySelector from "./currency-selector";

interface Category {
  id: string;
  name: string;
}

interface NavigationBarProps {
  categories: Category[];
  subcategories: Category[];
  className?: string;
  onCurrencyChange?: (currency: string) => void;
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  categories,
  subcategories,
  className,
  onCurrencyChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTypesOpen, setIsTypesOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedCurrency, setSelectedCurrency] = useState("SAR");

  const handleSearch = (query: string) => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setIsOpen(false);
    }
  };

  const handleCategoryTap = (categoryId: string) => {
    if (categoryId.trim()) {
      navigate(`/category?categoryId=${categoryId}`);
      setIsOpen(false);
    }
  };

  const handleSubCategoryTap = (subCategoryId: string) => {
    if (subCategoryId.trim()) {
      navigate(`/category?subcategoryId=${subCategoryId}`);
      setIsOpen(false);
    }
  };

  return (
    <div className={`${className}`}>
      {/* Desktop Navigation */}
      <nav className="hidden lg:block fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        {/* Top bar with logo and search */}
        <div className="border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
            <SellexxLogo withText className="h-14" />
            <SearchBar onSearch={handleSearch} className="max-w-md w-full" />
          </div>
        </div>

        {/* Categories menu */}
        <div className="border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex items-center space-x-8">
              {categories.map((category) => (
                <a
                  key={category.id}
                  onClick={() => handleCategoryTap(category.id)}
                  className="py-4 text-gray-700 hover:text-primary-darker-green transition-colors duration-200 font-medium"
                >
                  {category.name}
                </a>
              ))}

              {/* Types dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center space-x-1 py-4 text-gray-700 hover:text-primary-darker-green transition-colors duration-200 font-medium group"
                  onClick={() => setIsTypesOpen(!isTypesOpen)}
                >
                  <span>Types</span>
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
                </button>

                <div className="absolute top-full left-0 w-64 bg-white shadow-lg rounded-br-2xl rounded-bl-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-100">
                  {subcategories.map((sub, i) => (
                    <a
                      onClick={() => handleSubCategoryTap(sub.id)}
                      key={sub.id}
                      className={`${
                        i === subcategories.length - 1
                          ? "rounded-br-2xl rounded-bl-2xl"
                          : ""
                      } block px-6 py-3 text-gray-700 hover:bg-primary-grey hover:text-primary-darker-green transition-colors duration-200`}
                    >
                      {sub.name}
                    </a>
                  ))}
                </div>
              </div>
              {/* <div className="currency-div flex">
                <CurrencySelector onCurrencyChange={ 
                  onCurrencyChange ? onCurrencyChange : setSelectedCurrency
                } />
              </div> */}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className="lg:hidden">
        <button
          onClick={() => {
            if ("vibrate" in navigator) {
              navigator.vibrate(30);
            }
            return setIsOpen(!isOpen);
          }}
          className="fixed top-4 right-4 z-50 p-2 bg-white rounded-full shadow-lg transition-transform duration-200 hover:scale-110"
        >
          <div className="relative w-6 h-6">
            <Menu
              className={`absolute w-6 h-6 transition-transform duration-300 ${
                isOpen
                  ? "opacity-0 rotate-90 scale-75"
                  : "opacity-100 rotate-0 scale-100"
              }`}
            />
            <X
              className={`absolute w-6 h-6 transition-transform duration-300 ${
                isOpen
                  ? "opacity-100 rotate-0 scale-100"
                  : "opacity-0 -rotate-90 scale-75"
              }`}
            />
          </div>
        </button>

        <div
          className={`
            fixed inset-0 bg-primary-grey z-40 pt-20
            transition-all duration-300 ease-in-out
            ${
              isOpen
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-full"
            }
          `}
        >
          <div className="px-4 py-4 border-b border-gray-200">
            <SearchBar onSearch={handleSearch} className="max-w-md w-full" />
          </div>

          <div className="px-4 py-2">
            {/* Regular categories */}
            {categories.map((category) => (
              <a
                onClick={() => handleCategoryTap(category.id)}
                key={category.id}
                className="block py-4 text-gray-700 hover:text-primary-darker-green transition-colors duration-200 font-medium border-b border-gray-100"
              >
                {category.name}
              </a>
            ))}

            {/* Types section */}
            <div
              className={`$ {
                isTypesOpen ? "shadow-xl rounded-2xl" : ""
              } transition-shadow duration-300 border-b border-gray-100`}
            >
              <button
                onClick={() => setIsTypesOpen(!isTypesOpen)}
                className="flex items-center justify-between w-full py-4 font-medium"
              >
                <span>Types</span>
                <ChevronDown
                  className={`w-5 h-5 transform transition-transform duration-300 ${
                    isTypesOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`
        
                  pl-4 bg-white rounded-b-2xl
                  transition-all duration-300 ease-in-out overflow-hidden
                  ${isTypesOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}
                `}
              >
                {subcategories.map((sub) => (
                  <a
                    onClick={() => handleSubCategoryTap(sub.id)}
                    key={sub.id}
                    className="block px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    {sub.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div />
    </div>
  );
};

export default NavigationBar;
